import React, { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import MessageText, { formatTime } from "./MessageText";
import EmptyState from "./EmptyState";
import { useLanguage } from "../../i18n/LanguageContext";

/**
 * MessageList — scrollable conversation feed.
 *
 * Handles three visual states:
 *  - loading existing history
 *  - empty state (welcome + suggestions)
 *  - messages stream + optional typing indicator
 *
 * Receives a forwarded ref so the parent can auto-scroll to bottom.
 */
const MessageList = forwardRef(function MessageList({
  messages, sending, loadingHistory, locale, onPickSuggestion,
}, ref) {
  const { t } = useLanguage();
  return (
    <div
      ref={ref}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
      style={{ fontFamily: "JetBrains Mono, monospace" }}
    >
      {loadingHistory && (
        <div className="text-center py-6 text-[#A0AEC0] text-xs tracking-widest uppercase">
          {t("chat.loading_history")}
        </div>
      )}

      {!loadingHistory && messages.length === 0 && (
        <EmptyState onPickSuggestion={onPickSuggestion} />
      )}

      {messages.map((m, i) => (
        <div
          key={`${m.role}-${m.created_at || i}`}
          data-testid={`chat-msg-${m.role}`}
          className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
        >
          <div
            className={`max-w-[86%] text-xs leading-relaxed px-3 py-2 ${
              m.role === "user"
                ? "bg-[#00E5FF]/15 border border-[#00E5FF]/30 text-white"
                : "bg-[#050505] border border-white/[0.04] text-[#D1D5DB]"
            }`}
          >
            <MessageText text={m.text} />
            <div className="text-[9px] text-[#A0AEC0] mt-1 tracking-widest uppercase">
              {m.role === "user" ? t("chat.you") : t("chat.alterego")} · {formatTime(m.created_at, locale)}
            </div>
          </div>
        </div>
      ))}

      {sending && (
        <div className="flex justify-start">
          <div className="bg-[#050505] border border-white/[0.04] px-3 py-2 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 text-[#00E5FF] animate-spin" />
            <span className="text-xs text-[#CBD5E1]">{t("chat.thinking")}</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default MessageList;
